"use client";

import {
  type ContentType,
  ContentTypeContentEnum,
  type EventType,
  gqlClient,
  MAIN_SITE_ROUTES,
  type PostType,
  type SearchType,
  type SpeakerType,
} from "@frontend/roz-sdk-core";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@frontend/roz-uikit/studio";
import { useQuery } from "@tanstack/react-query";
import { SearchIcon } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { navigateSdkHref } from "@/lib";
import { NavLink } from "@/primitives";
import { useSdk } from "@/provider";

const SEARCH_RESULTS_QUERY = `
  query HeaderSearchResults($query: String, $outputType: String) {
    search(query: $query, outputType: $outputType) {
      events {
        id
        slug
        name
      }
      content {
        id
        slug
        title
        typeContent
      }
      speakers {
        id
        firstName
        lastName
        nickname
      }
      news {
        id
        slug
        title
      }
    }
  }
`;

const SEARCH_THROTTLE_MS = 1000;
const MIN_SEARCHING_CHARS_COUNT = 3;

type SearchResponse = {
  search?: SearchType | null;
};

type SearchResultType =
  | "article"
  | "event"
  | "podcast"
  | "post"
  | "speaker"
  | "video";

const useThrottledValue = <T,>(value: T, interval = 500): T => {
  const [throttledValue, setThrottledValue] = useState<T>(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setThrottledValue(value);
    }, interval);

    return () => clearTimeout(timeoutId);
  }, [interval, value]);

  return throttledValue;
};

const buildResultHref = (
  type: SearchResultType,
  item: EventType & ContentType & PostType & SpeakerType
) => {
  switch (type) {
    case "event":
      return `${MAIN_SITE_ROUTES.events}/${item.slug}`;
    case "video":
      return `${MAIN_SITE_ROUTES.library}/video/${item.slug}?from=cinema`;
    case "article":
      return `${MAIN_SITE_ROUTES.library}/article/${item.slug}`;
    case "podcast":
      return `${MAIN_SITE_ROUTES.library}/podcast/${item.slug}`;
    case "post":
      return `${MAIN_SITE_ROUTES.news}/${item.slug}`;
    case "speaker":
      return `${MAIN_SITE_ROUTES.speakers}/${item.id}`;
    default:
      return MAIN_SITE_ROUTES.search;
  }
};

const getResultTitle = (
  type: SearchResultType,
  item: EventType & ContentType & PostType & SpeakerType
) => {
  switch (type) {
    case "event":
      return item.name;
    case "speaker":
      return (
        item.nickname || `${item.lastName || ""} ${item.firstName || ""}`.trim()
      );
    default:
      return item.title;
  }
};

const SearchResults = ({
  hideSearch,
  phrase,
}: {
  hideSearch: () => void;
  phrase: string;
}) => {
  const { data, isLoading } = useQuery({
    enabled: phrase.length >= MIN_SEARCHING_CHARS_COUNT,
    queryFn: async () =>
      await gqlClient.request<SearchResponse>(SEARCH_RESULTS_QUERY, {
        outputType: "mobile",
        query: phrase,
      }),
    queryKey: ["header-search", phrase],
  });

  const results = data?.search;
  const eventsList = (results?.events?.filter(Boolean) || []) as EventType[];
  const newsList = (results?.news?.filter(Boolean) || []) as PostType[];
  const speakersList = (results?.speakers?.filter(Boolean) ||
    []) as SpeakerType[];
  const contentList = (results?.content?.filter(Boolean) ||
    []) as ContentType[];

  const videosList = contentList.filter(
    (item) => item.typeContent === ContentTypeContentEnum.Video
  );
  const articlesList = contentList.filter(
    (item) => item.typeContent === ContentTypeContentEnum.Article
  );
  const podcastsList = contentList.filter(
    (item) => item.typeContent === ContentTypeContentEnum.Podcast
  );

  const groups = [
    { items: eventsList, title: "Мероприятия", type: "event" as const },
    { items: newsList, title: "Новости", type: "post" as const },
    { items: videosList, title: "Видео", type: "video" as const },
    { items: articlesList, title: "Статьи", type: "article" as const },
    { items: podcastsList, title: "Подкасты", type: "podcast" as const },
    { items: speakersList, title: "Лекторы", type: "speaker" as const },
  ];

  const hasResults = groups.some(({ items }) => items.length > 0);

  if (isLoading) {
    return (
      <p className="sdkui:py-4 sdkui:text-center sdkui:text-dark-60 sdkui:text-sm">
        Ищем...
      </p>
    );
  }

  if (!hasResults) {
    return (
      <p className="sdkui:py-4 sdkui:text-center sdkui:text-dark-60 sdkui:text-sm">
        Ничего не найдено
      </p>
    );
  }

  return (
    <div className="sdkui:space-y-6">
      {groups.map(({ items, title, type }) => {
        if (items.length === 0) {
          return null;
        }

        return (
          <section className="sdkui:space-y-3" key={title}>
            <h3 className="sdkui:font-bold sdkui:text-dark-50 sdkui:text-xxs sdkui:uppercase sdkui:tracking-[0.12em]">
              {title}
            </h3>
            <ul className="sdkui:space-y-2.5">
              {items.map((item) => (
                <li key={item.id}>
                  <NavLink
                    className="sdkui:line-clamp-1 sdkui:text-dark-100 sdkui:text-sm sdkui:transition-colors hover:sdkui:text-primary-50"
                    href={buildResultHref(
                      type,
                      item as EventType & ContentType & PostType & SpeakerType
                    )}
                    onClick={hideSearch}
                  >
                    {getResultTitle(
                      type,
                      item as EventType & ContentType & PostType & SpeakerType
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export const SearchTrigger = () => {
  const sdk = useSdk();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const throttledSearch = useThrottledValue(searchValue, SEARCH_THROTTLE_MS);

  const normalizedSearchValue = searchValue.trim();
  const normalizedThrottledSearch = throttledSearch.trim();
  const isSearchPopoverOpen =
    searchInputFocused &&
    normalizedThrottledSearch.length >= MIN_SEARCHING_CHARS_COUNT;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (normalizedSearchValue.length === 0) {
      return;
    }

    setSearchInputFocused(false);
    navigateSdkHref(
      sdk,
      `${MAIN_SITE_ROUTES.search}?query=${encodeURIComponent(
        normalizedSearchValue
      )}`
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setSearchInputFocused(false);
    }
  };

  const isSearchContainerTarget = (target: EventTarget | null) => {
    return (
      target instanceof Node && searchContainerRef.current?.contains(target)
    );
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setSearchInputFocused(false);
        }
      }}
      open={isSearchPopoverOpen}
    >
      <PopoverAnchor asChild>
        <div className="sdkui:grow" ref={searchContainerRef}>
          <form
            className="sdkui:relative sdkui:z-300 sdkui:flex sdkui:w-full sdkui:items-center"
            onSubmit={handleSubmit}
          >
            <InputGroup className="sdkui:h-10 sdkui:border-zinc-100 sdkui:bg-white sdkui:shadow-none sdkui:outline-none sdkui:ring-0 has-[[data-slot=input-group-control]:focus-visible]:sdkui:border-zinc-100 has-[[data-slot=input-group-control]:focus]:sdkui:border-zinc-100 has-[[data-slot=input-group-control]:focus-visible]:sdkui:ring-0 has-[[data-slot=input-group-control]:focus]:sdkui:ring-0">
              <InputGroupAddon
                align="inline-start"
                className="sdkui:pr-0 sdkui:text-dark-60"
              >
                <SearchIcon className="sdkui:size-4" />
              </InputGroupAddon>
              <InputGroupInput
                className="sdkui:h-full sdkui:appearance-none sdkui:border-0 sdkui:px-0 sdkui:pl-2 sdkui:text-dark-100 sdkui:text-sm sdkui:shadow-none sdkui:outline-none sdkui:ring-0 [-webkit-appearance:none] sdkui:placeholder:text-dark-60 focus:sdkui:border-0 focus:sdkui:outline-none focus:sdkui:ring-0 focus-visible:sdkui:outline-none focus-visible:sdkui:ring-0"
                name="query"
                onChange={(event) => setSearchValue(event.target.value)}
                onFocus={() => setSearchInputFocused(true)}
                onKeyDown={handleKeyDown}
                placeholder="Мероприятия, фильмы, лекторы"
                type="text"
                value={searchValue}
              />
            </InputGroup>
            <button
              className="sdkui:sr-only"
              disabled={normalizedSearchValue.length === 0}
              type="submit"
            >
              Найти
            </button>
          </form>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        className="sdkui:max-h-110 sdkui:w-(--radix-popover-trigger-width) sdkui:overflow-hidden sdkui:p-0 landscape:sdkui:max-h-[75dvh]"
        onFocusOutside={(event) => {
          if (isSearchContainerTarget(event.detail.originalEvent.target)) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (isSearchContainerTarget(event.detail.originalEvent.target)) {
            event.preventDefault();
          }
        }}
        onOpenAutoFocus={(event) => event.preventDefault()}
        side="bottom"
        sideOffset={8}
      >
        <div className="sdkui:h-auto sdkui:w-full sdkui:overflow-y-auto sdkui:px-5 sdkui:py-6">
          <SearchResults
            hideSearch={() => setSearchInputFocused(false)}
            phrase={normalizedThrottledSearch}
          />
          <NavLink
            className="sdkui:mt-6 sdkui:inline-flex sdkui:items-center sdkui:gap-2 sdkui:font-semibold sdkui:text-dark-100 sdkui:text-sm sdkui:transition-colors hover:sdkui:text-primary-50"
            href={`${MAIN_SITE_ROUTES.search}?query=${encodeURIComponent(
              normalizedThrottledSearch
            )}`}
            onClick={() => setSearchInputFocused(false)}
          >
            <SearchIcon className="sdkui:size-4" />
            Смотреть все результаты
          </NavLink>
        </div>
      </PopoverContent>
    </Popover>
  );
};
